/**
 * Centralized error handling for API requests
 */

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public info?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;

/**
 * Error categories for specific handling
 */
export type ErrorCategory = 'auth' | 'notfound' | 'forbidden' | 'validation' | 'server' | 'network' | 'unknown';

export function getErrorCategory(error: unknown): ErrorCategory {
  if (!isApiError(error)) {
    return 'network';
  }

  switch (error.status) {
    case 401:
    case 403:
      return 'auth';
    case 404:
      return 'notfound';
    case 400:
    case 422:
      return 'validation';
    case 500:
    case 502:
    case 503:
      return 'server';
    default:
      return 'unknown';
  }
}

/**
 * Determine if an error is retryable
 */
export function isRetryableError(error: unknown): boolean {
  if (!isApiError(error)) {
    return true; // Network errors might be retryable
  }

  // Don't retry auth, validation, or not found errors
  if ([400, 401, 403, 404, 422].includes(error.status)) {
    return false;
  }

  // Retry server errors and others
  return true;
}

/**
 * Determine if error should be suppressed (not shown to user)
 */
export function shouldSuppressError(status: number, context?: string): boolean {
  // Suppress known non-critical errors in specific contexts
  if (context === 'congregation' && status === 403) {
    return true; // User is not a congregation member - suppress
  }
  if (context === 'quote' && status === 404) {
    return true; // No quote for today - suppress
  }
  return false;
}
